import axios from "../../axios";
import logger from "../../shared/Logger";
import { buildApiV3Url } from "../../shared/Utils";
import { AppDispatch, RootState } from "../types";
import { Preference, setError, setIsLoadingPreferences, setPreferences } from "./index";

interface PreferencesResponse {
    value?: Preference[];
}

/**
 * Fetches all ECM preferences and stores them in Redux
 * Relies on Redux state sharing to prevent duplicate calls
 */
export const fetchAllPreferences =
    () =>
    async (dispatch: AppDispatch, getState: () => RootState): Promise<Preference[]> => {
        const { preferences, isLoadingPreferences } = getState().preferences;

        // Return existing data if already loaded
        if (preferences.length > 0) {
            return preferences;
        }

        // Prevent multiple simultaneous requests
        if (isLoadingPreferences) {
            return preferences;
        }

        dispatch(setIsLoadingPreferences(true));

        try {
            const response = await axios.get<PreferencesResponse>(
                buildApiV3Url("ecm-preferences"),
                {
                    withCredentials: true,
                }
            );

            const preferencesData: Preference[] = response?.data?.value || [];
            dispatch(setPreferences(preferencesData));
            dispatch(setIsLoadingPreferences(false));
            return preferencesData;
        } catch {
            const errorMessage = "Failed to fetch preferences";
            logger(2, "error", {
                message: "Error fetching ECM preferences:",
            });
            dispatch(setError(errorMessage));
            dispatch(setIsLoadingPreferences(false));
            return [];
        }
    };

/**
 * Gets a specific preference by name from Redux store
 */
export const getPreference =
    (preferenceName: string) =>
    (dispatch: AppDispatch, getState: () => RootState): Preference | null => {
        const { preferences } = getState().preferences;
        return preferences.find(pref => pref.preference === preferenceName) || null;
    };

/**
 * Gets a parsed preference value by name from Redux store
 */
export const getPreferenceValue =
    (preferenceName: string) =>
    (dispatch: AppDispatch): unknown => {
        const preference = dispatch(getPreference(preferenceName)) as Preference | null;
        if (!preference?.preferenceValue) {
            return null;
        }

        try {
            return JSON.parse(preference.preferenceValue);
        } catch {
            logger(2, "error", {
                message: `Error parsing preference value for ${preferenceName}:`,
            });
            return null;
        }
    };

/**
 * Gets multiple preferences in a single call from Redux store
 */
export const getMultiplePreferences =
    (preferenceNames: string[]) =>
    (dispatch: AppDispatch, getState: () => RootState): Record<string, unknown> => {
        const { preferences } = getState().preferences;
        const result: Record<string, unknown> = {};

        preferenceNames.forEach(name => {
            const preference = preferences.find(
                pref => pref.preference === name
            ) as Preference | null;
            if (preference?.preferenceValue) {
                try {
                    result[name] = JSON.parse(preference.preferenceValue);
                } catch {
                    logger(2, "error", {
                        message: `Error parsing preference value for ${name}:`,
                    });
                    result[name] = null;
                }
            } else {
                result[name] = null;
            }
        });

        return result;
    };

/**
 * Ensures preferences are loaded before getting specific preferences
 */
export const ensurePreferencesLoaded =
    () =>
    async (dispatch: AppDispatch, getState: () => RootState): Promise<Preference[]> => {
        const { preferences, isLoadingPreferences } = getState().preferences;

        // If we have data, return it
        if (preferences.length > 0) {
            return preferences;
        }

        // If already loading, return the current preferences (they might be empty but loading is in progress)
        if (isLoadingPreferences) {
            return preferences;
        }

        // Otherwise, fetch data
        return await dispatch(fetchAllPreferences());
    };
